/**
 * reCAPTCHA v2 "I'm not a robot" checkbox widget.
 * Loads the Google reCAPTCHA script on demand and renders the widget.
 * @param {string} siteKey - reCAPTCHA site key from backend
 * @param {function} onChange - Called with token (string) when user completes captcha, or '' when reset
 * @param {string} [theme] - 'light' or 'dark'
 */
import { useEffect, useRef, useCallback } from 'react';

const RECAPTCHA_SCRIPT_URL = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';

export default function RecaptchaWidget({ siteKey, onChange, theme = 'light' }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const loadScript = useCallback(() => {
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.render) {
        resolve();
        return;
      }
      const existing = document.querySelector('script[src*="recaptcha"]');
      if (existing) {
        const checkReady = () => {
          if (window.grecaptcha && window.grecaptcha.render) {
            resolve();
          } else {
            setTimeout(checkReady, 50);
          }
        };
        checkReady();
        return;
      }
      window.onRecaptchaLoad = resolve;
      const script = document.createElement('script');
      script.src = RECAPTCHA_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    });
  }, []);

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;

    let mounted = true;
    loadScript().then(() => {
      if (!mounted || !containerRef.current || !window.grecaptcha) return;
      if (widgetIdRef.current != null) return;
      try {
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => onChangeRef.current && onChangeRef.current(token),
          'expired-callback': () => onChangeRef.current && onChangeRef.current(''),
        });
      } catch (e) {
        console.error('reCAPTCHA render error:', e);
        widgetIdRef.current = null;
      }
    });

    return () => {
      mounted = false;
      if (widgetIdRef.current != null && window.grecaptcha && window.grecaptcha.reset) {
        try {
          window.grecaptcha.reset(widgetIdRef.current);
        } catch (e) {}
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme, loadScript]);

  if (!siteKey) return null;

  return (
    <div className="flex justify-center my-4">
      <div ref={containerRef} data-testid="recaptcha-widget" />
    </div>
  );
}
